import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  PencilIcon,
  CheckIcon,
  DocumentIcon,
  DocumentArrowDownIcon,
  PhotoIcon,
  MapPinIcon,
  BoltIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { UTILITIES } from 'utils/utilities';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import TermsAgreementCheckbox from 'components/TermsAgreementCheckbox';

import { StepShell, HelpedBlock, ImagePlaceholder } from '../atoms';

// Match Step 3 — promoter-side form is EUR-denominated.
const CURRENCY_SYMBOL = '€';

const Stat = ({ icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-line shadow-card p-4">
    <div className="flex items-center gap-2 text-gray-500">
      <span className="w-6 h-6 grid place-items-center rounded-lg bg-paper-2">
        {icon}
      </span>
      <p className="text-[10px] font-bold tracking-widest uppercase">{label}</p>
    </div>
    <p className="mt-1.5 text-[18px] font-extrabold text-ink-strong">{value}</p>
  </div>
);

const ReviewSection = ({ title, onEdit, children, editLabel }) => (
  <div className="bg-white rounded-2xl border border-line shadow-card overflow-hidden">
    <header className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-line">
      <h3 className="text-[14px] font-bold text-ink-strong tracking-tight">
        {title}
      </h3>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-800 hover:text-amber-900"
        >
          <PencilIcon className="w-3.5 h-3.5" /> {editLabel}
        </button>
      )}
    </header>
    <div className="px-5 py-4 space-y-4">{children}</div>
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-3 items-start">
    <div className="col-span-12 sm:col-span-3">
      <p className="text-[10px] font-bold tracking-widest uppercase text-muted">
        {label}
      </p>
    </div>
    <div className="col-span-12 sm:col-span-9 text-[13px] text-ink-strong">
      {value || <span className="text-muted">—</span>}
    </div>
  </div>
);

const CollapsibleText = ({ text, t }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = (text?.length || 0) > 240;
  const clamp =
    !expanded && isLong
      ? {
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : undefined;

  return (
    <div>
      <div
        className="text-[13px] text-gray-700 whitespace-pre-wrap break-words"
        style={clamp}
      >
        {text}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-bold text-amber-800 hover:text-amber-900 underline"
        >
          {expanded
            ? t('common.show_less', 'Show less')
            : t('common.show_more', 'Show more')}
        </button>
      )}
    </div>
  );
};

const Step5 = ({
  goToStep,
  agreedToTerms,
  onAgreeChange,
  termsError,
  publishLocked = false,
}) => {
  const { t, i18n } = useTranslation();
  const { getValues } = useFormContext();
  const values = getValues();

  const currencySymbol = CURRENCY_SYMBOL;

  const totalUnits = (values.spaces || []).reduce(
    (s, sp) => s + (Number(sp.quantity) || 0),
    0
  );
  const totalRevenue = (values.spaces || []).reduce(
    (s, sp) => s + (Number(sp.quantity) || 0) * (Number(sp.price) || 0),
    0
  );

  // Honor the actual schedule type: a fixed run uses start/end, while a
  // segmented run is described by its list of days. Guarding on `type` avoids
  // surfacing stale start/end values left behind after switching modes.
  const isSegmented = values.date?.type === 'segmented';
  const startDate =
    !isSegmented && values.date?.startDate
      ? new Date(values.date.startDate + 'T00:00:00')
      : null;
  const endDate =
    !isSegmented && values.date?.endDate
      ? new Date(values.date.endDate + 'T00:00:00')
      : null;
  const dateLine =
    startDate && endDate
      ? `${startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
      : isSegmented && values.date?.days?.length
        ? `${values.date.days.length} ${t('event_create.specific_days_count', 'specific days')}`
        : '—';

  // Extract macro + micro IDs for the read-only CategorySelectorGrid below.
  const macroIds = (values.categoriesAllowed || [])
    .map((c) => c?.macroCategoryId)
    .filter((id) => id != null);
  const microIds = (values.categoriesAllowed || []).flatMap((c) =>
    Array.isArray(c?.microCategoryIds) ? c.microCategoryIds : []
  );

  // Human labels for the on-site amenities list. Falls back gracefully for
  // custom amenities (which are stored as their raw label in customAmenities).
  const lang = (i18n?.language || 'en').startsWith('pt') ? 'pt' : 'en';
  const utilityLabel = (id) => {
    if (typeof id === 'string' && id.startsWith('custom:')) {
      // Try to find the user-typed label in customAmenities by slug match
      const slug = id.replace('custom:', '');
      const match = (values.customAmenities || []).find(
        (c) => c.toLowerCase().replace(/\s+/g, '_') === slug
      );
      return match || slug.replace(/_/g, ' ');
    }
    const u = UTILITIES.find((x) => x.id === id);
    return u?.labels?.[lang] || u?.labels?.en || id;
  };

  const avatarSrc =
    values.avatarPreview || values.avatar || values.eventIcon?.preview || null;
  const landscapeSrc =
    values.landscapePreview || values.landscapeImage || null;
  const additionalSrcs =
    values.additionalPreviews ||
    values.morePictures?.map((p) => (typeof p === 'string' ? p : p?.preview)) ||
    [];

  const edit = (n) => () => goToStep && goToStep(n);
  const editLabel = t('common.edit', 'Edit');

  return (
    <StepShell
      eyebrow={t('event_create.step5_eyebrow', 'Step 05 / Review & publish')}
      title={t('event_create.step5_title', 'One last look')}
      lede={t(
        'event_create.step5_lede',
        'Review the event card brands will see. Anything you want to change? Jump back, edit, and come back here.'
      )}
    >
      <HelpedBlock sectionKey="reviewSummary">
      {/* Hero preview */}
      <div className="bg-white rounded-2xl border border-line shadow-card overflow-hidden">
        <div className="relative h-44 placeholder-amber overflow-hidden">
          {landscapeSrc ? (
            <img
              src={landscapeSrc}
              alt="cover"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <span className="mono text-[11px] tracking-widest text-amber-700/70 uppercase">
                {t('event_create.cover_preview', 'Cover image preview')}
              </span>
            </div>
          )}
          <div className="absolute left-5 -bottom-6 w-16 h-16 rounded-2xl ring-4 ring-white shadow-card overflow-hidden placeholder grid place-items-center">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="icon"
                className="w-full h-full object-cover"
              />
            ) : (
              <PhotoIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>
          <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-line text-[10px] font-bold tracking-widest text-ink-strong uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 softpulse" />
            {t('event_create.preview', 'Preview')}
          </div>
        </div>
        <div className="pt-9 pb-5 px-5">
          <h3 className="text-[20px] font-extrabold text-ink-strong tracking-tight">
            {values.name || t('event_create.untitled_event', 'Untitled event')}
          </h3>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {values.location ||
              t('event_create.no_location', 'No location set')}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-paper-2 border border-line text-[11px] font-bold text-ink-strong">
              <CalendarIcon className="w-3.5 h-3.5" /> {dateLine}
            </span>
            {values.date?.type === 'fixed' &&
              values.date?.openHours &&
              values.date?.closeHours && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-paper-2 border border-line text-[11px] font-bold text-ink-strong">
                  {values.date.openHours}–{values.date.closeHours}
                </span>
              )}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat
          icon={<CalendarIcon className="w-3.5 h-3.5" />}
          label={t('event_create.kpi_schedule', 'Schedule')}
          value={
            values.date?.type === 'fixed'
              ? t('event_create.schedule_fixed_short', 'Continuous run')
              : `${values.date?.days?.length || 0} ${t('event_create.specific_days_count', 'specific days')}`
          }
        />
        <Stat
          icon={<MapPinIcon className="w-3.5 h-3.5" />}
          label={t('event_create.kpi_units', 'Bookable units')}
          value={`${totalUnits} ${t('event_create.kpi_units_suffix', 'places')}`}
        />
        <Stat
          icon={<BoltIcon className="w-3.5 h-3.5" />}
          label={t('event_create.kpi_revenue', 'Max revenue')}
          value={`${currencySymbol}${totalRevenue.toLocaleString('en-GB')}`}
        />
      </div>
      </HelpedBlock>

      {/* Details */}
      <HelpedBlock sectionKey="reviewDetails">
      <ReviewSection
        title={t('event_create.details', 'Details')}
        onEdit={edit(1)}
        editLabel={editLabel}
      >
        <ReviewRow
          label={t('event_create.short_description')}
          value={values.shortDescription}
        />
        <ReviewRow
          label={t('event_create.long_description')}
          value={
            values.longDescription ? (
              <CollapsibleText text={values.longDescription} t={t} />
            ) : null
          }
        />
        {macroIds.length > 0 && (
          <ReviewRow
            label={t('event_create.categories')}
            value={
              <CategorySelectorGrid
                size="xs"
                readOnly
                restrictToSelected
                initialCategories={{ macros: macroIds, micros: microIds }}
              />
            }
          />
        )}
        <ReviewRow
          label={t('event_create.web', 'Web')}
          value={
            values.website ||
            values.instagramUrl ||
            values.facebookUrl ||
            values.tiktokUrl ||
            values.linkedinUrl ? (
              <div className="flex flex-wrap items-center gap-3 text-[12px]">
                {values.website && (
                  <span className="font-semibold text-ink-strong">
                    🌐 {values.website.replace(/^https?:\/\//, '')}
                  </span>
                )}
                {values.instagramUrl && (
                  <span className="text-gray-600">
                    📷{' '}
                    {values.instagramUrl.replace(/^https?:\/\/(www\.)?/, '')}
                  </span>
                )}
              </div>
            ) : null
          }
        />
      </ReviewSection>
      </HelpedBlock>

      {/* Media & amenities */}
      <HelpedBlock sectionKey="reviewMedia">
      <ReviewSection
        title={t('event_create.media_amenities', 'Media & amenities')}
        onEdit={edit(2)}
        editLabel={editLabel}
      >
        <ReviewRow
          label={t('event_create.cover', 'Cover')}
          value={
            <div className="flex items-center gap-3">
              {landscapeSrc ? (
                <img
                  src={landscapeSrc}
                  alt="cover"
                  className="w-40 aspect-[16/9] object-cover rounded-xl border border-line"
                />
              ) : (
                <ImagePlaceholder ratio="16/9" label="cover" className="w-40" />
              )}
            </div>
          }
        />
        <ReviewRow
          label={t('event_create.gallery_label', 'Gallery')}
          value={
            additionalSrcs?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {additionalSrcs.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`gallery ${i + 1}`}
                    className="w-14 h-14 object-cover rounded-lg border border-line"
                  />
                ))}
              </div>
            ) : null
          }
        />
        <ReviewRow
          label={t('event_create.regulatory_document', 'Regulatory document')}
          value={
            values.regulatoryPreview || values.regulatoryDocument ? (
              <a
                href={values.regulatoryPreview || values.regulatoryDocument}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[12.5px] font-bold text-ink-strong hover:text-amber-700"
              >
                <span className="w-7 h-7 grid place-items-center rounded-lg bg-rose-50 text-rose-600">
                  <DocumentIcon className="w-4 h-4" />
                </span>
                {values.regulatoryName ||
                  t('event_create.regulatory_document')}
                <DocumentArrowDownIcon className="w-3.5 h-3.5" />
              </a>
            ) : null
          }
        />
        <ReviewRow
          label={t('event_create.required_documents', 'Required Documents')}
          value={
            values.requiredDocuments?.length ? (
              <ul className="space-y-1.5">
                {values.requiredDocuments.map((d, i) => (
                  <li
                    key={i}
                    className="inline-flex items-center gap-2 text-[12.5px] text-gray-700 mr-3"
                  >
                    <CheckIcon className="w-3.5 h-3.5 text-emerald-600" /> {d}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
        <ReviewRow
          label={t('event_create.amenities_card', 'Provided on-site')}
          value={
            values.utilities?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {(values.utilities || []).map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-softer border border-amber-200 text-[11.5px] font-bold text-amber-900"
                  >
                    {utilityLabel(id)}
                  </span>
                ))}
              </div>
            ) : null
          }
        />
      </ReviewSection>
      </HelpedBlock>

      {/* Spaces */}
      <HelpedBlock sectionKey="reviewSpaces">
      <ReviewSection
        title={t('event_create.spaces', 'Spaces')}
        onEdit={edit(3)}
        editLabel={editLabel}
      >
        <div className="rounded-xl border border-line bg-paper-2 overflow-hidden">
          <div className="grid grid-cols-12 text-[10px] font-bold tracking-widest uppercase text-muted px-4 py-2.5 border-b border-line bg-white">
            <div className="col-span-5">
              {t('event_create.space_name', 'Space')}
            </div>
            <div className="col-span-2 text-right">
              {t('event_create.size', 'Size')}
            </div>
            <div className="col-span-2 text-right">
              {t('event_create.qty', 'Qty')}
            </div>
            <div className="col-span-3 text-right">
              {t('event_create.price_no_taxes', 'Price')}
            </div>
          </div>
          {(values.spaces || []).map((sp, i) => (
            <div
              key={i}
              className="grid grid-cols-12 items-center px-4 py-3 border-b border-line last:border-0"
            >
              <div className="col-span-5">
                <p className="text-[13px] font-bold text-ink-strong">
                  {sp.name}
                </p>
                <p className="text-[11px] text-muted truncate">
                  {sp.description}
                </p>
              </div>
              <div className="col-span-2 text-right text-[13px] font-semibold tabular-nums text-gray-700">
                {sp.size} m²
              </div>
              <div className="col-span-2 text-right text-[13px] font-semibold tabular-nums text-gray-700">
                {sp.quantity}
              </div>
              <div className="col-span-3 text-right text-[13px] font-extrabold tabular-nums text-ink-strong">
                {currencySymbol}
                {Number(sp.price || 0).toLocaleString('en-GB')}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-12 px-4 py-3 bg-white">
            <div className="col-span-5 text-[11px] font-bold tracking-widest uppercase text-muted">
              {t('event_create.total_max_revenue', 'Total max revenue')}
            </div>
            <div className="col-span-7 text-right text-[15px] font-extrabold text-ink-strong tabular-nums">
              {currencySymbol}
              {totalRevenue.toLocaleString('en-GB')}
            </div>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-muted">
          {t(
            'event_create.review_price_note',
            'Prices shown exclude VAT. Brands pay this amount plus VAT and the t2b fee.'
          )}
        </p>
      </ReviewSection>
      </HelpedBlock>

      {/* Floor plan */}
      {(values.floorplanPreview || values.floorplanUrl) && (
        <HelpedBlock sectionKey="reviewFloorplan">
        <ReviewSection
          title={t('event_create.floor_plan', 'Floor plan')}
          onEdit={edit(4)}
          editLabel={editLabel}
        >
          <div className="relative w-full max-w-md mx-auto">
            <img
              src={values.floorplanPreview || values.floorplanUrl}
              alt="floor plan"
              className="w-full rounded-xl border border-line"
            />
            {(values.spacePlacements || []).map((p, i) => (
              <div
                key={p.id ?? i}
                className="absolute bg-ink-strong text-brand text-[10px] px-1.5 py-0.5 rounded-full shadow -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${(p.xPercent ?? p.x ?? 0) * (p.xPercent != null ? 100 : 1)}%`,
                  top: `${(p.yPercent ?? p.y ?? 0) * (p.yPercent != null ? 100 : 1)}%`,
                }}
                title={p.spaceName || p.label}
              >
                {(p.spaceName || p.label || `P${i + 1}`)
                  ?.slice(0, 2)
                  .toUpperCase()}
              </div>
            ))}
          </div>
        </ReviewSection>
        </HelpedBlock>
      )}

      {publishLocked && (
        // T2B-423: the footer only offers "Save draft" here — say why before
        // the promoter goes looking for the publish button.
        <div className="max-w-2xl mx-auto flex items-start gap-3 rounded-2xl border border-brand/40 bg-brand-softer p-4">
          <InformationCircleIcon className="w-5 h-5 shrink-0 text-brand-deep mt-0.5" />
          <div className="text-[13px] leading-relaxed text-ink">
            <p className="font-bold text-ink-strong">
              {t('event_create.draft_notice_title')}
            </p>
            <p className="mt-0.5">{t('event_create.draft_notice_body')}</p>
          </div>
        </div>
      )}

      <div className="flex justify-center pt-2">
        <TermsAgreementCheckbox
          checked={agreedToTerms}
          onChange={onAgreeChange}
          error={termsError}
          className="max-w-2xl"
        />
      </div>
    </StepShell>
  );
};

export default Step5;
